import React, { useState } from 'react'
import { Button, useNotifications } from '@wallyhealth/web-core'
import { CardNumberElement, CardExpiryElement, CardCvcElement, useStripe, useElements } from '@stripe/react-stripe-js'
import { useNavigate } from 'react-router-dom'
import classes from 'classnames'
import styles from './index.module.css'
import { centsToDollarsTrunc } from 'utils/billingUtils'

const resolveStripeFontSize = () => {
  return (window.innerWidth > 768) ? 14 : 14
}

const createStripeOptions = (options = {}) => {
  const fontSize = resolveStripeFontSize()

  return {
    placeholder: options.placeholder,
    style: {
      base: {
        fontSize: `${fontSize}px`,
        fontWeight: '100',
        fontSmoothing: 'antialiased',
        color: 'black',
        '::placeholder': {
          color: '#838383'
        },
        ':-webkit-autofill': {
          color: '#fce883'
        }
      },
      invalid: {
        iconColor: '#F92E32',
        color: '#F92E32'
      }
    }
  }
}

const userFriendlyErrorTypes = ['card_error', 'validation_error']

const userFriendlyErrorMessages = [
  'Wally account already exists'
]

const CardPayOption = ({ buttonClassName, totalCostInCents, recordPaymentClick, prepSubscription }) => {
  const notifications = useNotifications()
  const stripe = useStripe()
  const elements = useElements()
  const navigate = useNavigate()

  const [isRunningPayment, setIsRunningPayment] = useState(false)
  const [isCardNumberComplete, setIsCardNumberComplete] = useState(false)
  const [isCardExpiryComplete, setIsCardExpiryComplete] = useState(false)
  const [isCardCvcComplete, setIsCardCvcComplete] = useState(false)

  const onCardNumberChanged = ({ complete }) => {
    setIsCardNumberComplete(complete)
  }

  const onCardExpiryChanged = ({ complete }) => {
    setIsCardExpiryComplete(complete)
  }

  const onCardCvcChanged = ({ complete }) => {
    setIsCardCvcComplete(complete)
  }

  const areInputsValid = () => {
    return isCardNumberComplete && isCardExpiryComplete && isCardCvcComplete
  }

  const runPayment = async () => {
    setIsRunningPayment(true)

    try {
      const { clientSecret, patientId } = await prepSubscription()
      await confirmCardPayment({ clientSecret, patientId })
    } catch (err) {
      tryHandleError(err)
    } finally {
      setIsRunningPayment(false)
    }
  }

  const confirmCardPayment = async ({ clientSecret, patientId }) => {
    const data = {
      payment_method: {
        card: elements.getElement(CardNumberElement)
      }
    }

    const { paymentIntent, error } = await stripe.confirmCardPayment(clientSecret, data)

    tryHandleError(error)
    tryHandleSuccessfulPayment({ paymentIntent, patientId })
  }

  const tryHandleSuccessfulPayment = ({ paymentIntent, patientId }) => {
    if (paymentIntent && paymentIntent.status === 'succeeded') {
      navigate(`/welcome?pid=${patientId}`)
    }
  }

  const tryHandleError = (error) => {
    if (error) {
      handleError(error)
    }
  }

  const handleError = (error) => {
    const isUserFriendly = userFriendlyErrorTypes.includes(error.type) || userFriendlyErrorMessages.includes(error.message)

    const message = isUserFriendly
      ? error.message
      : 'Oops, something went wrong. Please try again.'

    notifications.error(message)
  }

  const handleSubmit = async () => {
    if (!isRunningPayment) {
      recordPaymentClick({ clickName: 'PayClick' })
      runPayment()
    }
  }

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <div className={styles.card_element}>
          <CardNumberElement
            onChange={onCardNumberChanged}
            options={createStripeOptions({ placeholder: 'Card number' })}
          />
        </div>

        <div className={styles.card_columns}>
          <div className={styles.card_expiry}>
            <CardExpiryElement
              options={createStripeOptions()}
              onChange={onCardExpiryChanged}
            />
          </div>

          <div className={styles.card_cvc}>
            <CardCvcElement
              options={createStripeOptions()}
              onChange={onCardCvcChanged}
            />
          </div>
        </div>
      </div>

      <Button
        className={classes(styles.button, buttonClassName)}
        label={`Pay $${centsToDollarsTrunc(totalCostInCents)} by card`}
        disabled={!areInputsValid()}
        onClick={handleSubmit}
        isLoading={isRunningPayment}
      />

    </div>
  )
}

export default CardPayOption
